/**
 * Templates de mensagem — a galeria.
 *
 * E-mail mostra a miniatura do HTML de verdade (com variáveis de exemplo);
 * SMS/WhatsApp mostram o texto — porque texto É a cara deles. Criar pede só
 * canal e nome e cai direto no editor: template vazio não ajuda ninguém na
 * listagem.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { api, type Item, type Lista } from '../../api/cliente'
import { MiniEmail } from '../../componentes/templates/MiniEmail'
import {
  Aviso,
  Botao,
  Campo,
  Cartao,
  Carregando,
  Etiqueta,
  Modal,
  Selecao,
  Vazio,
  formatarData,
} from '../../componentes/ui'
import { NOME_CANAL, type Template } from './tipos'

export function ListagemTemplates() {
  const navegar = useNavigate()
  const qc = useQueryClient()
  const [criando, setCriando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [form, setForm] = useState({ nome: '', canal: 'email' as Template['canal'] })

  const { data, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<Lista<Template>>('/api/v1/templates'),
  })

  const criar = useMutation({
    mutationFn: () =>
      api.post<Item<Template>>('/api/v1/templates', {
        nome: form.nome,
        canal: form.canal,
        ...(form.canal === 'email'
          ? {
              assunto: form.nome,
              corpoHtml:
                '<!doctype html><html lang="pt-BR"><body style="margin:0;background:#f8fafc;font-family:system-ui,sans-serif"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:32px 16px"><table role="presentation" width="100%" style="max-width:600px;background:#fff;border:1px solid #e2e8f0;border-radius:12px"><tr><td style="background:#0c64b0;border-radius:12px 12px 0 0;padding:18px 32px;color:#fff;font-weight:600">EGBA — Diário Oficial do Estado da Bahia</td></tr><tr><td style="padding:32px;font-size:15px;line-height:1.65;color:#0f172a"><p style="margin:0 0 16px">Olá, {{primeiroNome}}.</p><p style="margin:0 0 16px">Escreva aqui o conteúdo do e-mail.</p><p style="margin:24px 0"><a href="{{linkLanding}}" style="background:#ff6b35;color:#fff;text-decoration:none;padding:13px 26px;border-radius:8px;font-weight:600;display:inline-block">Botão de ação</a></p><p style="margin:24px 0 0;font-size:13px;color:#64748b">EGBA — Empresa Gráfica da Bahia · publicacoes@egba.ba.gov.br · (71) 3116-2850</p></td></tr></table></td></tr></table></body></html>',
              corpoTexto: 'Olá, {{primeiroNome}}. Escreva aqui a versão texto. {{linkLanding}}',
            }
          : { corpoTexto: 'Escreva aqui a mensagem. {{linkLanding}}' }),
      }),
    onSuccess: (r) => {
      setCriando(false)
      void qc.invalidateQueries({ queryKey: ['templates'] })
      void navegar(`/templates/${r.dados.id}`)
    },
    onError: (e: Error) => setErro(e.message),
  })

  if (isLoading) return <Carregando />

  return (
    <div className="space-y-6">
      <Cartao
        titulo="Templates de mensagem"
        descricao="O conteúdo que as réguas enviam. E-mail com HTML completo, remetente e pré-header próprios; SMS e WhatsApp em texto. Variáveis {{assim}} são preenchidas por destinatário no envio."
        acoes={<Botao onClick={() => setCriando(true)}>Novo template</Botao>}
      >
        {data?.dados.length ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.dados.map((t) => (
              <Link
                key={t.id}
                to={`/templates/${t.id}`}
                className="flex flex-col overflow-hidden rounded-xl border border-[color:var(--color-borda)] transition hover:border-marca-300 hover:shadow-sm"
              >
                {t.canal === 'email' && t.corpoHtml ? (
                  <div className="pointer-events-none h-44 overflow-hidden bg-slate-50">
                    <MiniEmail html={t.corpoHtml} altura={340} />
                  </div>
                ) : (
                  <div className="flex h-44 items-start bg-slate-50 p-4">
                    <p className="line-clamp-6 rounded-xl rounded-tl-sm bg-white p-3 text-[13px] leading-relaxed text-slate-700 shadow-sm">
                      {t.corpoTexto}
                    </p>
                  </div>
                )}
                <div className="flex flex-1 flex-col gap-1.5 border-t border-[color:var(--color-borda)] p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="min-w-0 flex-1 truncate font-medium text-slate-900">{t.nome}</p>
                    <Etiqueta tom={t.canal === 'email' ? 'marca' : 'sucesso'}>
                      {NOME_CANAL[t.canal]}
                    </Etiqueta>
                  </div>
                  {t.assunto && <p className="truncate text-xs text-slate-500">{t.assunto}</p>}
                  <p className="text-xs text-slate-400">{formatarData(t.atualizadoEm)}</p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <Vazio
            titulo="Nenhum template ainda"
            descricao="Crie o primeiro: cole seu HTML, edite no visual ou peça para a IA montar."
            acao={<Botao onClick={() => setCriando(true)}>Novo template</Botao>}
          />
        )}
      </Cartao>

      <Modal aberto={criando} aoFechar={() => setCriando(false)} titulo="Novo template">
        <div className="space-y-4">
          {erro && <Aviso tom="perigo">{erro}</Aviso>}
          <Campo
            rotulo="Nome"
            obrigatorio
            value={form.nome}
            onChange={(e) => setForm({ ...form, nome: e.target.value })}
            ajuda="É como ele aparece na hora de escolher na régua."
          />
          <Selecao
            rotulo="Canal"
            value={form.canal}
            onChange={(e) => setForm({ ...form, canal: e.target.value as Template['canal'] })}
            opcoes={[
              { valor: 'email', texto: 'E-mail — HTML completo' },
              { valor: 'sms', texto: 'SMS — texto' },
              { valor: 'whatsapp', texto: 'WhatsApp — texto' },
            ]}
          />
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <Botao variante="secundario" onClick={() => setCriando(false)}>
            Cancelar
          </Botao>
          <Botao
            carregando={criar.isPending}
            disabled={form.nome.trim().length < 3}
            onClick={() => criar.mutate()}
          >
            Criar e abrir editor
          </Botao>
        </div>
      </Modal>
    </div>
  )
}
